"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoyaltyEarnRulesSchema = void 0;
exports.computeLoyaltyEarn = computeLoyaltyEarn;
const zod_1 = require("zod");
/**
 * Pure, POS-parity loyalty "earn" calculator.
 *
 * This is a verbatim port of the base points formula in
 * `services/agenticpos/pos-transaction-processor/src/steps/sale/processLoyalty.ts`
 * (lines 165-239 as of the port). It MUST stay in lock-step with that file —
 * do NOT drift the arithmetic here without also updating POS, and vice versa.
 *
 * Deliberately excluded (by design, matching POS):
 * - The first-purchase bonus. POS adds `earnRules.firstPurchaseBonus.points`
 *   AFTER rounding, gated on a live `visitCount === 1` read from the
 *   customer record — that's an I/O-dependent decision this pure function
 *   has no business making. Callers (e.g. the accrue endpoint) apply it
 *   separately, exactly like the POS pipeline does.
 * - Persistence (ADD loyaltyPoints/lifetimePoints/visitCount/totalSpent...).
 *   This function only returns the number; callers own the write.
 */
/** Earn-relevant subset of the LOYALTY_RULES settings document. */
exports.LoyaltyEarnRulesSchema = zod_1.z.object({
    programActive: zod_1.z.boolean().optional(),
    earnRules: zod_1.z
        .object({
        baseRate: zod_1.z.number().optional(),
        rounding: zod_1.z.enum(['floor', 'ceil', 'round']).optional(),
        tieredEnabled: zod_1.z.boolean().optional(),
        tiers: zod_1.z
            .array(zod_1.z.object({ min: zod_1.z.number(), max: zod_1.z.number(), rate: zod_1.z.number() }))
            .optional(),
        categoryBonuses: zod_1.z
            .array(zod_1.z.object({ categoryId: zod_1.z.string(), multiplier: zod_1.z.number() }))
            .optional(),
        events: zod_1.z
            .array(zod_1.z.object({
            startDate: zod_1.z.string(),
            endDate: zod_1.z.string(),
            appliesTo: zod_1.z.string(),
            categoryId: zod_1.z.string().optional(),
            multiplier: zod_1.z.number(),
        }))
            .optional(),
        firstPurchaseBonus: zod_1.z
            .object({ enabled: zod_1.z.boolean().optional(), points: zod_1.z.number().optional() })
            .optional(),
    })
        .optional(),
    tierConfig: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(),
        threshold: zod_1.z.number().optional(),
        earnMultiplier: zod_1.z.number().optional(),
    }))
        .optional(),
    pointCalculation: zod_1.z
        .object({
        includeTax: zod_1.z.boolean().optional(),
        includeFees: zod_1.z.boolean().optional(),
        includeTips: zod_1.z.boolean().optional(),
    })
        .optional(),
});
/** POS processLoyalty.ts roundPoints — verbatim. */
function roundPoints(value, mode) {
    if (mode === 'ceil')
        return Math.ceil(value);
    if (mode === 'round')
        return Math.round(value);
    return Math.floor(value);
}
/** POS processLoyalty.ts calculateTieredPoints — verbatim (tier.max === 0 means uncapped). */
function calculateTieredPoints(total, tiers) {
    let points = 0;
    for (const tier of tiers) {
        if (total <= tier.min)
            break;
        const max = tier.max === 0 ? Infinity : tier.max;
        const applicableAmount = Math.min(total, max) - tier.min;
        if (applicableAmount > 0)
            points += applicableAmount * tier.rate;
    }
    return points;
}
/**
 * Compute base loyalty points earned for a cart, identically to the POS sale
 * pipeline's processLoyalty step. Returns the rounded base `points` plus a
 * `breakdown` for observability (does NOT include the first-purchase bonus).
 */
function computeLoyaltyEarn(rules, ctx) {
    const earnRules = rules.earnRules || {};
    const tierConfig = rules.tierConfig || [];
    const rounding = earnRules.rounding || 'floor';
    const pointCalc = rules.pointCalculation || {};
    const now = ctx.now;
    // Find active double-points events (POS: earnRules.events || []).
    const activeEvents = (earnRules.events || []).filter((e) => e.startDate <= now && e.endDate >= now);
    // --- Calculate points earned ---
    let totalPoints = 0;
    for (const line of ctx.lines) {
        // The POS wire payload renames cart-state `loyaltyEarnsPoints` →
        // `loyaltyConfig.earnsPoints`. Read both for forward/backward compat but
        // treat loyaltyConfig as the canonical shape (matches POS).
        const loyaltyConfig = line.loyaltyConfig || {};
        const earnsPoints = typeof loyaltyConfig.earnsPoints === 'boolean'
            ? loyaltyConfig.earnsPoints
            : typeof line.loyaltyEarnsPoints === 'boolean'
                ? line.loyaltyEarnsPoints
                : undefined;
        // Custom items earn points only when explicitly opted in
        // (loyaltyEarnsPoints/loyaltyConfig.earnsPoints === true). Non-custom
        // items must have a variantId; earnsPoints === false excludes them too.
        if (line.isCustom) {
            if (earnsPoints !== true)
                continue;
        }
        else {
            if (!line.variantId)
                continue;
            if (earnsPoints === false)
                continue;
        }
        const lineTotal = line.lineTotal || 0;
        // Base points: tiered brackets or simple rate.
        let itemPoints;
        if (earnRules.tieredEnabled && earnRules.tiers?.length) {
            itemPoints = calculateTieredPoints(lineTotal, earnRules.tiers);
        }
        else {
            itemPoints = lineTotal * (earnRules.baseRate || 1);
        }
        // Product-level multiplier override takes priority over category bonus.
        if (loyaltyConfig.overrideEnabled && loyaltyConfig.multiplier && loyaltyConfig.multiplier !== 1) {
            itemPoints *= loyaltyConfig.multiplier;
        }
        else if (line.categoryId && earnRules.categoryBonuses?.length) {
            const catBonus = earnRules.categoryBonuses.find((b) => b.categoryId === line.categoryId);
            if (catBonus)
                itemPoints *= catBonus.multiplier;
        }
        // Event multiplier(s) stack on top.
        for (const evt of activeEvents) {
            if (evt.appliesTo === 'all' || (evt.appliesTo === 'category' && evt.categoryId === line.categoryId)) {
                itemPoints *= evt.multiplier;
            }
        }
        totalPoints += itemPoints;
    }
    const base = totalPoints;
    // Add tax/fees/tips to points base if configured.
    if (pointCalc.includeTax && ctx.taxTotal)
        totalPoints += ctx.taxTotal * (earnRules.baseRate || 1);
    if (pointCalc.includeFees && ctx.feesTotal)
        totalPoints += ctx.feesTotal * (earnRules.baseRate || 1);
    if (pointCalc.includeTips && ctx.tipAmount)
        totalPoints += ctx.tipAmount * (earnRules.baseRate || 1);
    const afterExtras = totalPoints;
    // Tier multiplier stacks on top (defaults to 'bronze' when unset, matching POS).
    const customerTier = ctx.memberTier || 'bronze';
    const tierDef = tierConfig.find((t) => t.id === customerTier);
    if (tierDef && tierDef.earnMultiplier !== undefined && tierDef.earnMultiplier !== 1) {
        totalPoints *= tierDef.earnMultiplier;
    }
    const afterTier = totalPoints;
    const points = roundPoints(totalPoints, rounding);
    return { points, breakdown: { base, afterExtras, afterTier } };
}
//# sourceMappingURL=loyalty-earn.js.map